import { Component } from '@theme/component';

/**
 * The footer newsletter signup (blocks/email-signup.liquid).
 *
 * Two jobs, both tied to Shopify's native customer-form round trip.
 *
 * 1. Scroll correction. Shopify's redirect appends `#<form id>` to the URL and
 *    the browser jumps straight to the `<form>`. The heading/intro copy above
 *    this form is a separate sibling block, so that native jump lands the
 *    viewport past it. This nudges the scroll back to the top of the enclosing
 *    section once the page has settled.
 *
 * 2. Bloomreach Engagement identity and events (SM-754). The native form is a
 *    plain HTML POST, so a signup reached Bloomreach only through the
 *    server-side Shopify customer import — which has no browser context and so
 *    attaches no `cookie` soft ID. The resulting profile carried the email but
 *    none of that visitor's browsing history, unlike signups from the
 *    newsletter weblayer. Calling `identify` here, in the browser, merges the
 *    anonymous cookie profile into the `email_id` (email) profile, which is
 *    what attaches the cookie.
 *
 *    The SDK cannot be called at submit time: the page navigates immediately
 *    and the request is lost. So submit stashes the normalised values in
 *    sessionStorage, and the success render — which only exists when Shopify
 *    actually accepted the signup — picks them up and fires. The cookie
 *    survives the reload, so the profile is the same one that was browsing.
 *
 * @extends {Component}
 */

const PENDING_STORAGE_KEY = 'newsletter-signup-pending';
// A stash is only ever consumed by the very next page view. Anything older is
// an abandoned tab, so expire it rather than letting it fire out of context.
const PENDING_TTL_MS = 10 * 60 * 1000;
// The Loomi app embed loads the Engagement SDK on its own schedule, and unlike
// the other Bloomreach components this one runs at page load rather than on a
// user gesture, so the SDK genuinely may not be up yet.
const SDK_POLL_INTERVAL = 250;
const SDK_POLL_ATTEMPTS = 20;
// Set by blocks/email-signup.liquid on the success branch only.
const SUCCESS_MESSAGE_ID_PREFIX = 'Email-signup__message-success-';

/**
 * @typedef {object} PendingSignup
 * @property {string} formId
 * @property {string} email
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} source
 * @property {number} at
 */

export class EmailSignup extends Component {
  /** @type {number | undefined} */
  #sdkPollTimer;

  /**
   * The block's own root element, which carries the `data-*` Bloomreach
   * contract. Not the host: Shopify generates the `email-signup-component`
   * wrapper from the schema's `tag`, so nothing the block's Liquid writes can
   * land on it.
   *
   * @returns {DOMStringMap}
   */
  get #config() {
    const root = this.querySelector('.email-signup-block');
    return root instanceof HTMLElement ? root.dataset : {};
  }

  connectedCallback() {
    super.connectedCallback();

    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    form.addEventListener('submit', this.#onSubmit);

    this.#correctScrollPosition(form);
    this.#flushPendingSignup(form);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.querySelector('form')?.removeEventListener('submit', this.#onSubmit);
    window.clearTimeout(this.#sdkPollTimer);
  }

  /** @param {HTMLFormElement} form */
  #correctScrollPosition(form) {
    if (!form.id || window.location.hash !== `#${form.id}`) return;

    const section = this.closest('.section') ?? this;
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Splits the single visible name field the way the newsletter weblayer does,
   * so both paths build the same Bloomreach profile, then stashes the values
   * for the success render. Never blocks the native submit.
   *
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    const emailInput = form.querySelector('input[name="contact[email]"]');
    const email = emailInput instanceof HTMLInputElement ? emailInput.value.trim().toLowerCase() : '';
    if (!email) return;

    const nameInput = form.querySelector('[data-signup-name]');
    const lastNameInput = form.querySelector('[data-signup-last-name]');

    let firstName = '';
    let lastName = '';

    if (nameInput instanceof HTMLInputElement) {
      const parts = nameInput.value.trim().split(/\s+/).filter(Boolean);
      firstName = parts[0] ?? '';
      lastName = parts.slice(1).join(' ');

      // Shopify stores whatever these hold, so split them too — otherwise
      // "Jane Smith" becomes a first name of "Jane Smith".
      if (lastName && lastNameInput instanceof HTMLInputElement) {
        nameInput.value = firstName;
        lastNameInput.value = lastName;
      }
    }

    try {
      sessionStorage.setItem(
        PENDING_STORAGE_KEY,
        JSON.stringify({
          formId: form.id,
          email,
          firstName,
          lastName,
          source: this.#config.signupSource ?? '',
          at: Date.now(),
        })
      );
    } catch {
      // Private browsing or a full quota. The signup itself still works; only
      // the cookie stitch is lost, and the Shopify import still creates the
      // profile.
    }
  };

  /**
   * Reads a stash left by this form's own submit and, once the SDK is up,
   * sends it to Bloomreach. Runs only on the render that carries Shopify's
   * success message, so nothing is sent for a rejected signup.
   *
   * @param {HTMLFormElement} form
   */
  #flushPendingSignup(form) {
    if (!this.querySelector(`[id^="${SUCCESS_MESSAGE_ID_PREFIX}"]`)) return;

    /** @type {PendingSignup | null} */
    let pending = null;
    try {
      const raw = sessionStorage.getItem(PENDING_STORAGE_KEY);
      if (!raw) return;
      pending = JSON.parse(raw);
    } catch {
      return;
    }

    // Two customer forms can coexist on a page (this block and the signup
    // popup), and a back-button revisit can re-render a success state whose
    // stash has already been spent.
    if (!pending?.email || pending.formId !== form.id) return;

    if (!Number.isFinite(pending.at) || Date.now() - pending.at > PENDING_TTL_MS) {
      this.#clearPendingSignup();
      return;
    }

    this.#awaitSdk(pending, 0);
  }

  /**
   * @param {PendingSignup} pending
   * @param {number} attempt - How many polls have already run.
   */
  #awaitSdk(pending, attempt) {
    const sdk = [window.exponea, window.brweb].find(
      (candidate) => typeof candidate?.track === 'function' && typeof candidate?.identify === 'function'
    );

    if (sdk) {
      // Spent before sending, not after: a duplicate consent event is worse
      // than a dropped one, and the Shopify import still carries the signup.
      this.#clearPendingSignup();
      this.#send(sdk, pending);
      return;
    }

    if (attempt >= SDK_POLL_ATTEMPTS) {
      this.#clearPendingSignup();
      console.warn('[email-signup] Bloomreach Engagement SDK never loaded; signup not stitched to the cookie');
      return;
    }

    this.#sdkPollTimer = window.setTimeout(() => this.#awaitSdk(pending, attempt + 1), SDK_POLL_INTERVAL);
  }

  /**
   * @param {{identify: Function, track: Function}} sdk
   * @param {PendingSignup} pending
   */
  #send(sdk, pending) {
    const { email, firstName, lastName, source } = pending;
    const { bloomreachConsentCategory: consentCategory, bloomreachEventName: eventName } = this.#config;

    // A blank name must not be sent: Bloomreach would overwrite a name the
    // profile already holds with an empty string.
    /** @type {Record<string, string>} */
    const name = {};
    if (firstName) name.first_name = firstName;
    if (lastName) name.last_name = lastName;

    try {
      // First, so the events below land on the merged profile. The hard ID is
      // the lowercased email, per Bloomreach's guidance and matching the
      // weblayer — a different casing would build a second profile. It must be
      // `email_id`, the ID this project and the Shopify import key on: the
      // project ignores `registered`, leaving the cookie profile anonymous.
      sdk.identify({ email_id: email }, { email, ...name });

      if (consentCategory) {
        sdk.track('consent', {
          action: 'accept',
          category: consentCategory,
          valid_until: 'unlimited',
          message: 'Footer newsletter signup',
          location: window.location.href,
        });
      }

      if (eventName) {
        sdk.track(eventName, {
          email,
          ...name,
          source,
          location: window.location.href,
        });
      }
    } catch (error) {
      // Shopify has already rendered the success message and the subscriber
      // exists, so there is nothing to tell the shopper.
      console.warn('[email-signup] Bloomreach tracking failed:', error);
    }
  }

  #clearPendingSignup() {
    try {
      sessionStorage.removeItem(PENDING_STORAGE_KEY);
    } catch {
      // Nothing to do — see #onSubmit.
    }
  }
}

if (!customElements.get('email-signup-component')) {
  customElements.define('email-signup-component', EmailSignup);
}
